import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppLayout from '@/components/AppLayout';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Clipboard,
  CheckCircle,
  Info,
  MonitorSmartphone,
  Sparkles,
  MessageSquareText
} from 'lucide-react';

export default function ImportPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [groupName, setGroupName] = useState('');
  const [textContent, setTextContent] = useState('');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const fileInputRef = useRef(null);

  const isStarterPlan = (stats?.plan || user?.plan) === 'starter';
  const normalizedGroupName = groupName.trim().toLowerCase();
  const isExistingGroupName = groups.some((group) => String(group?.name || '').trim().toLowerCase() === normalizedGroupName);
  const starterLimitReachedForNewGroup = isStarterPlan && (stats?.groups_remaining || 0) <= 0 && !isExistingGroupName;

  const loadStats = async () => {
    try {
      const response = await fetch(`${API}/stats`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load import stats', error);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await fetch(`${API}/groups`, {
        credentials: 'include',
      });

      if (response.ok) {
        setGroups(await response.json());
      }
    } catch (error) {
      console.error('Failed to load groups', error);
    }
  };

  useEffect(() => {
    loadStats();
    loadGroups();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const presetGroupName = params.get('groupName');
    if (presetGroupName) {
      setGroupName(presetGroupName);
    }
  }, [location.search]);

  const getImportedCount = () => {
    if (result?.messages_received != null) {
      return result.messages_received;
    }
    return result?.message?.match(/\d+/)?.[0] || 0;
  };

  const getNewMessagesCount = () => {
    if (result?.new_messages != null) {
      return result.new_messages;
    }
    return result?.message?.match(/\d+/)?.[0] || 0;
  };

  const handleSelectedFile = (nextFile) => {
    if (!nextFile) {
      return;
    }

    const normalizedName = nextFile.name.toLowerCase();
    if (!normalizedName.endsWith('.txt') && !normalizedName.endsWith('.zip')) {
      toast.error('Use um arquivo .txt ou .zip exportado da conversa');
      return;
    }

    setFile(nextFile);
  };

  const handleTextImport = async () => {
    if (!textContent.trim()) {
      toast.error('Cole o conteúdo das mensagens');
      return;
    }

    if (starterLimitReachedForNewGroup) {
      setShowLimitDialog(true);
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const response = await fetch(`${API}/import/text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          text: textContent,
          group_name: groupName || undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        await loadStats();
        await loadGroups();
        toast.success(`${data.message}`);
        setTextContent('');
        setGroupName('');
      } else {
        const error = await response.json();
        const detail = error.detail;
        if (detail?.code === 'GROUP_LIMIT_REACHED') {
          setShowLimitDialog(true);
          toast.error(detail.message);
        } else {
          toast.error(detail?.message || detail || 'Erro ao importar mensagens');
        }
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setImporting(false);
    }
  };

  const handleFileImport = async () => {
    if (!groupName.trim()) {
      toast.error('Preencha o nome do grupo antes de importar o arquivo');
      return;
    }

    if (!file) {
      toast.error('Selecione um arquivo');
      return;
    }

    if (starterLimitReachedForNewGroup) {
      setShowLimitDialog(true);
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (groupName) {
        formData.append('group_name', groupName);
      }

      const response = await fetch(`${API}/import/file`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        await loadStats();
        await loadGroups();
        toast.success(`${data.message}`);
        setFile(null);
        setIsDragging(false);
        setGroupName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        const error = await response.json();
        const detail = error.detail;
        if (detail?.code === 'GROUP_LIMIT_REACHED') {
          setShowLimitDialog(true);
          toast.error(detail.message);
        } else {
          toast.error(detail?.message || detail || 'Erro ao importar arquivo');
        }
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setImporting(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-6 md:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900 flex items-center gap-3">
            <Upload className="w-8 h-8 text-violet-600" />
            Importar Conversas
          </h1>
          <p className="text-zinc-500 mt-1">
            Traga conversas para o Zapt Resumo e receba contexto, prioridades e respostas sugeridas
          </p>
        </div>

        <Card className={`mb-6 ${isStarterPlan ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className={`text-sm font-medium mb-1 ${isStarterPlan ? 'text-amber-800' : 'text-emerald-700'}`}>
                  {isStarterPlan ? 'Limite do plano Starter' : 'Seu plano permite importar mais grupos'}
                </p>
                <p className={`text-sm ${isStarterPlan ? 'text-amber-700' : 'text-emerald-600'}`}>
                  {isStarterPlan
                    ? `Você já importou ${stats?.groups_count || 0} de ${stats?.groups_limit || 2} grupos. Atualizações do mesmo grupo continuam liberadas.`
                    : 'Você pode continuar importando novos grupos e atualizar os que já existem.'}
                </p>
              </div>
              <div className={`rounded-xl px-4 py-3 text-right ${isStarterPlan ? 'bg-white/80' : 'bg-white/70'}`}>
                <p className="text-xs uppercase tracking-wide text-zinc-500">Grupos importados</p>
                <p className="text-lg font-semibold text-zinc-900">
                  {stats?.groups_count || 0}
                  {isStarterPlan ? ` / ${stats?.groups_limit || 2}` : ''}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-700 font-medium mb-1">
                  Fluxo recomendado hoje:
                </p>
                <ol className="text-sm text-blue-600 space-y-1 list-decimal list-inside">
                  <li>Abra o grupo no app de mensagens e exporte a conversa, com ou sem mídia</li>
                  <li>Importe o arquivo ou cole o conteúdo aqui</li>
                  <li>Veja o resumo, as pendências e as respostas sugeridas</li>
                  <li>Arquivos .zip já trazem contexto adicional de mídia para enriquecer a análise</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {[
            {
              icon: MessageSquareText,
              title: 'Hoje: upload simples',
              description: 'Importe exportações de conversa e tenha valor imediato sem depender de integrações frágeis.'
            },
            {
              icon: Sparkles,
              title: 'Depois: rotina recorrente',
              description: 'Atualize grupos importantes com frequência e mantenha seu painel sempre útil.'
            },
            {
              icon: MonitorSmartphone,
              title: 'Próxima camada: app',
              description: 'A mesma base pode evoluir para web, desktop e compartilhamento direto do celular.'
            }
          ].map((item) => (
            <Card key={item.title} className="border-zinc-200">
              <CardContent className="p-4">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center mb-3">
                  <item.icon className="w-5 h-5" />
                </div>
                <p className="text-sm font-medium text-zinc-900 mb-1">{item.title}</p>
                <p className="text-sm text-zinc-600">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Import Tabs */}
        <Card className="border-zinc-200">
          <Tabs defaultValue="file" className="w-full">
            <CardHeader className="border-b border-zinc-100 pb-0">
              <TabsList className="bg-zinc-100 p-1">
                <TabsTrigger value="file" className="rounded-md">
                  <FileText className="w-4 h-4 mr-2" />
                  Upload de Arquivo
                </TabsTrigger>
                <TabsTrigger value="paste" className="rounded-md">
                  <Clipboard className="w-4 h-4 mr-2" />
                  Colar Texto
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent className="p-6">
              {/* Group Name Input (shared) */}
              <div className="mb-6">
                <Label htmlFor="groupName" className="text-sm font-medium text-zinc-700">
                  Nome do Grupo (opcional)
                </Label>
                <Input
                  id="groupName"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Ex: Família, Trabalho, Amigos..."
                  className="mt-2"
                  data-testid="group-name-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Para upload de arquivo, o nome do grupo deve ser preenchido. No Starter, novos grupos param em 2.
                </p>
                {groupName ? (
                  <p className="text-xs text-violet-600 mt-1">
                    {isExistingGroupName
                      ? 'Esse nome corresponde a um grupo já cadastrado. O envio será tratado como atualização.'
                      : 'Para atualizar um grupo já cadastrado, envie um novo arquivo usando exatamente este mesmo nome.'}
                  </p>
                ) : null}
              </div>

              <TabsContent value="file" className="mt-0">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="file" className="text-sm font-medium text-zinc-700">
                      Arquivo TXT ou ZIP
                    </Label>
                    <div className="mt-2">
                      <div 
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                          isDragging
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-zinc-300 hover:border-violet-400'
                        }`}
                        onClick={() => fileInputRef.current?.click()}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          setIsDragging(true);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDragging(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setIsDragging(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDragging(false);
                          handleSelectedFile(e.dataTransfer.files?.[0]);
                        }}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".txt,.zip"
                          onChange={(e) => handleSelectedFile(e.target.files?.[0])}
                          className="hidden"
                          data-testid="file-input"
                        />
                        <Upload className="w-10 h-10 mx-auto mb-3 text-zinc-400" />
                        {file ? (
                          <p className="text-sm text-zinc-700 font-medium">{file.name}</p>
                        ) : (
                          <>
                            <p className="text-sm text-zinc-600">
                              Clique para selecionar ou arraste e solte o export da conversa
                            </p>
                            <p className="text-xs text-zinc-400 mt-1">
                              Aceita `.txt` sem mídia ou `.zip` com export completo da conversa
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleFileImport}
                    disabled={!file || !groupName.trim() || importing}
                    className="w-full rounded-full"
                    data-testid="import-file-btn"
                  >
                    {importing ? 'Importando...' : 'Importar Arquivo'}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="paste" className="mt-0">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="textContent" className="text-sm font-medium text-zinc-700">
                      Conteúdo das Mensagens
                    </Label>
                    <Textarea
                      id="textContent"
                      value={textContent}
                      onChange={(e) => setTextContent(e.target.value)}
                      placeholder="Cole aqui o conteúdo exportado da conversa..."
                      className="mt-2 min-h-[200px] font-mono text-sm"
                      data-testid="text-content-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Formato esperado: [DD/MM/YYYY, HH:MM] Nome: Mensagem
                    </p>
                  </div>

                  <Button
                    onClick={handleTextImport}
                    disabled={!textContent.trim() || importing}
                    className="w-full rounded-full"
                    data-testid="import-text-btn"
                  >
                    {importing ? 'Importando...' : 'Importar Mensagens'}
                  </Button>
                </div>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        {/* Result */}
        {result && (
          <Card className="mt-6 border-emerald-200 bg-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-emerald-700 font-medium mb-2">
                    Importação concluída!
                  </p>
                  <div className="space-y-1 text-sm text-emerald-600">
                    <p>Grupo: <span className="font-medium">{result.group_name}</span></p>
                    <p>Mensagens recebidas no arquivo: <span className="font-medium">{getImportedCount()}</span></p>
                    <p>Mensagens novas adicionadas: <span className="font-medium">{getNewMessagesCount()}</span></p>
                    {(result.duplicates_skipped || 0) > 0 && (
                      <p>Mensagens duplicadas ignoradas: <span className="font-medium">{result.duplicates_skipped}</span></p>
                    )}
                    {(result.affected_dates || []).length > 0 && (
                      <p>Datas atualizadas: <span className="font-medium">{result.affected_dates.join(', ')}</span></p>
                    )}
                    <p>Itens para responder: <span className="font-medium">{result.needs_response}</span></p>
                    {(result.attachments_found || 0) > 0 && (
                      <p>Anexos detectados: <span className="font-medium">{result.attachments_found}</span></p>
                    )}
                    {(result.attachments_linked || 0) > 0 && (
                      <p>Anexos ligados a mensagens: <span className="font-medium">{result.attachments_linked}</span></p>
                    )}
                    {(result.attachments_analyzed || 0) > 0 && (
                      <p>Anexos analisados pela IA: <span className="font-medium">{result.attachments_analyzed}</span></p>
                    )}
                    {(result.attachments_with_transcript || 0) > 0 && (
                      <p>Áudios transcritos: <span className="font-medium">{result.attachments_with_transcript}</span></p>
                    )}
                    {(result.attachments_with_ocr || 0) > 0 && (
                      <p>Imagens/documentos com OCR: <span className="font-medium">{result.attachments_with_ocr}</span></p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4 bg-white"
                    onClick={() => window.location.href = `/app/groups/${result.group_id}`}
                    data-testid="view-group-btn"
                  >
                    Ver Grupo Importado
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <AlertDialog open={showLimitDialog} onOpenChange={setShowLimitDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Limite de grupos do Starter atingido</AlertDialogTitle>
              <AlertDialogDescription>
                Você já chegou ao máximo de grupos importados no plano Starter. Ainda pode subir novas atualizações dos grupos existentes, mas para importar um novo grupo é preciso fazer upgrade para o plano Plus.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Agora não</AlertDialogCancel>
              <AlertDialogAction onClick={() => { window.location.href = '/app/billing'; }}>
                Fazer upgrade para o Plus
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
